/**
 * Team Logo Resolver for TheOddsAPI widgets
 * Maps TheOddsAPI team names to local logo paths.
 *
 * Logo directories:
 *   public/logos/teams/mlb/  — .png (e.g., Yankees.png, Red Sox.png)
 *   public/logos/teams/nfl/  — .png (e.g., Eagles.png, 49ers.png)
 *   public/logos/teams/nba/  — .svg (e.g., Lakers.svg, Trail Blazers.svg)
 *   public/logos/teams/nhl/  — .png (e.g., Bruins.png, Golden Knights.png)
 *   public/logos/teams/cfb/  — numbered prefix format, not supported here
 *   public/logos/teams/ncaa/ — numbered prefix format, not supported here
 *   public/logos/leagues/    — MLB.png, NBA.png, NFL.svg, NHL.png
 */

import { getTeamLogo as getMappedTeamLogo, CBB_TEAM_NAMES, CFB_TEAM_NAMES, TEAM_NAMES } from './logoMaps.js'

// Sport key -> { league folder, file extension }
const SPORT_CONFIG = {
  'baseball_mlb':            { league: 'mlb', ext: 'png' },
  'americanfootball_nfl':    { league: 'nfl', ext: 'png' },
  'basketball_nba':          { league: 'nba', ext: 'svg' },
  'icehockey_nhl':           { league: 'nhl', ext: 'png' },
}

// ESPN CDN logo URL builder — works for any sport if you know the abbreviation
const ESPN_CDN = (sport, abbr) =>
  `https://a.espncdn.com/combiner/i?img=/i/teamlogos/${sport}/500/${abbr.toLowerCase()}.png&h=40&w=40`

const compactCollegeKey = (value) =>
  String(value || '').toLowerCase().replace(/[^a-z0-9]/g, '')

function resolveCollegeCanonical(teamName, mappedLeague) {
  const primaryNameMap = mappedLeague === 'ncaaf' ? CFB_TEAM_NAMES : CBB_TEAM_NAMES
  const nameCandidates = [
    ...Object.entries(primaryNameMap || {}),
    ...Object.entries(TEAM_NAMES || {}),
  ]
  const compactTeam = compactCollegeKey(teamName)
  let bestMatch = null
  let bestLength = 0

  nameCandidates.forEach(([code, displayName]) => {
    const compactDisplay = compactCollegeKey(displayName)
    if (!compactDisplay) return

    if (
      compactTeam === compactDisplay ||
      compactTeam.startsWith(compactDisplay) ||
      compactDisplay.startsWith(compactTeam)
    ) {
      if (compactDisplay.length > bestLength) {
        bestMatch = code
        bestLength = compactDisplay.length
      }
    }
  })

  return bestMatch
}

// Mapping of sport keys to ESPN sport path segments
const ESPN_SPORT_MAP = {
  'baseball_mlb': 'mlb',
  'americanfootball_nfl': 'nfl',
  'basketball_nba': 'nba',
  'icehockey_nhl': 'nhl',
  'americanfootball_ncaaf': 'ncaa/500',
  'basketball_ncaab': 'ncaa/500',
  'basketball_wncaab': 'ncaa/500',
  'basketball_ncaaw': 'ncaa/500',
}

/**
 * Special team name mappings where "last word" extraction doesn't work.
 * Key: full TheOddsAPI team name, Value: filename stem (without extension).
 */
const SPECIAL_NAMES = {
  // MLB
  'Boston Red Sox': 'Red Sox',
  'Chicago White Sox': 'White Sox',
  'Toronto Blue Jays': 'Blue Jays',
  'Tampa Bay Rays': 'Rays',
  // NFL
  'Green Bay Packers': 'Packers',
  'Tampa Bay Buccaneers': 'Buccaneers',
  'San Francisco 49ers': '49ers',
  'Kansas City Chiefs': 'Chiefs',
  'New England Patriots': 'Patriots',
  'New York Giants': 'Giants',
  'New York Jets': 'Jets',
  'Las Vegas Raiders': 'Raiders',
  'Los Angeles Rams': 'Rams',
  'Los Angeles Chargers': 'Chargers',
  'New Orleans Saints': 'Saints',
  // NBA
  'Oklahoma City Thunder': 'Thunder',
  'Golden State Warriors': 'Warriors',
  'Portland Trail Blazers': 'Trail Blazers',
  'New York Knicks': 'Knicks',
  'Brooklyn Nets': 'Nets',
  'San Antonio Spurs': 'Spurs',
  'Los Angeles Lakers': 'Lakers',
  'Los Angeles Clippers': 'Clippers',
  'New Orleans Pelicans': 'Pelicans',
  'Philadelphia 76ers': '76ers',
  'Minnesota Timberwolves': 'Timberwolves',
  // NHL
  'Tampa Bay Lightning': 'Lightning',
  'Columbus Blue Jackets': 'Blue Jackets',
  'Detroit Red Wings': 'Red Wings',
  'Toronto Maple Leafs': 'Maple Leafs',
  'New York Rangers': 'Rangers',
  'New York Islanders': 'Islanders',
  'New Jersey Devils': 'Devils',
  'San Jose Sharks': 'Sharks',
  'St Louis Blues': 'Blues',
  'St. Louis Blues': 'Blues',
  'Vegas Golden Knights': 'Golden Knights',
  'Los Angeles Kings': 'Kings',
}

/**
 * Get the logo path for a team given TheOddsAPI's full team name and sport key.
 * Returns a path like "/logos/teams/mlb/Yankees.png" or ESPN CDN URL for college sports.
 * Returns null if not resolvable.
 */
export function getTeamLogoPath(teamName, sportKey) {
  if (!teamName || !sportKey) return null

  // Try exact match first, then prefix match (e.g. baseball_mlb_preseason → baseball_mlb)
  let config = SPORT_CONFIG[sportKey]
  if (!config) {
    for (const [prefix, cfg] of Object.entries(SPORT_CONFIG)) {
      if (sportKey.startsWith(prefix)) { config = cfg; break }
    }
  }

  // For supported local logo leagues (MLB, NFL, NBA, NHL)
  if (config) {
    const special = SPECIAL_NAMES[teamName]
    if (special) return `/logos/teams/${config.league}/${special}.${config.ext}`

    const parts = teamName.split(' ')
    const shortName = parts[parts.length - 1]
    return `/logos/teams/${config.league}/${shortName}.${config.ext}`
  }

  // For college sports and others — try ESPN CDN with last word as abbreviation
  // Prefer the NCAA-aware logo map before falling back to ESPN.
  if (
    sportKey === 'americanfootball_ncaaf' ||
    sportKey === 'basketball_ncaab' ||
    sportKey === 'basketball_wncaab' ||
    sportKey === 'basketball_ncaaw'
  ) {
    const mappedLeague = sportKey === 'americanfootball_ncaaf'
      ? 'ncaaf'
      : (sportKey === 'basketball_wncaab' || sportKey === 'basketball_ncaaw')
        ? 'ncaaw'
        : 'ncaab'
    const mappedLogo = getMappedTeamLogo(teamName, mappedLeague)
    if (mappedLogo) return mappedLogo

    const resolvedCanonical = resolveCollegeCanonical(teamName, mappedLeague)
    if (resolvedCanonical) {
      const resolvedDisplayLogo = getMappedTeamLogo(resolvedCanonical, mappedLeague)
      if (resolvedDisplayLogo) return resolvedDisplayLogo
    }
  }

  // This won't always be correct but provides a best-effort fallback
  const espnSport = ESPN_SPORT_MAP[sportKey]
  if (!espnSport) {
    // Try prefix match for ESPN sport map too
    for (const [prefix, sport] of Object.entries(ESPN_SPORT_MAP)) {
      if (sportKey.startsWith(prefix)) {
        const parts = teamName.split(' ')
        const shortName = parts[parts.length - 1]
        return ESPN_CDN(sport, shortName)
      }
    }
    return null
  }
  const parts = teamName.split(' ')
  const shortName = parts[parts.length - 1]
  return ESPN_CDN(espnSport, shortName)
}

/**
 * Get the league logo path for a sport key.
 * Returns path like "/logos/leagues/MLB.png" or null.
 */
const LEAGUE_LOGOS = {
  'baseball_mlb':         '/logos/leagues/MLB.png',
  'americanfootball_nfl': '/logos/leagues/NFL.svg',
  'basketball_nba':       '/logos/leagues/NBA.png',
  'icehockey_nhl':        '/logos/leagues/NHL.png',
  'basketball_ncaab':     '/logos/leagues/CBB.svg',
  'basketball_wncaab':    '/logos/leagues/CBB.svg',
  'americanfootball_ncaaf':'/logos/leagues/CFB.svg',
  'tennis_wta_charleston_open': '/logos/leagues/TENNIS.svg',
  'mma_mixed_martial_arts': '/logos/leagues/MMA.svg',
}

export function getLeagueLogoPath(sportKey) {
  return LEAGUE_LOGOS[sportKey] || null
}

// Re-export old MLB-only API for backward compatibility
const ESPN_LOGO = (abbr) =>
  `https://a.espncdn.com/combiner/i?img=/i/teamlogos/mlb/500/${abbr.toLowerCase()}.png&h=40&w=40`

const TEAMS = [
  { abbr: 'ARI', name: 'Arizona Diamondbacks', short: 'D-backs', espn: 'ari' },
  { abbr: 'ATL', name: 'Atlanta Braves', short: 'Braves', espn: 'atl' },
  { abbr: 'BAL', name: 'Baltimore Orioles', short: 'Orioles', espn: 'bal' },
  { abbr: 'BOS', name: 'Boston Red Sox', short: 'Red Sox', espn: 'bos' },
  { abbr: 'CHC', name: 'Chicago Cubs', short: 'Cubs', espn: 'chc' },
  { abbr: 'CWS', name: 'Chicago White Sox', short: 'White Sox', espn: 'chw' },
  { abbr: 'CIN', name: 'Cincinnati Reds', short: 'Reds', espn: 'cin' },
  { abbr: 'CLE', name: 'Cleveland Guardians', short: 'Guardians', espn: 'cle' },
  { abbr: 'COL', name: 'Colorado Rockies', short: 'Rockies', espn: 'col' },
  { abbr: 'DET', name: 'Detroit Tigers', short: 'Tigers', espn: 'det' },
  { abbr: 'HOU', name: 'Houston Astros', short: 'Astros', espn: 'hou' },
  { abbr: 'KC',  name: 'Kansas City Royals', short: 'Royals', espn: 'kc' },
  { abbr: 'LAA', name: 'Los Angeles Angels', short: 'Angels', espn: 'laa' },
  { abbr: 'LAD', name: 'Los Angeles Dodgers', short: 'Dodgers', espn: 'lad' },
  { abbr: 'MIA', name: 'Miami Marlins', short: 'Marlins', espn: 'mia' },
  { abbr: 'MIL', name: 'Milwaukee Brewers', short: 'Brewers', espn: 'mil' },
  { abbr: 'MIN', name: 'Minnesota Twins', short: 'Twins', espn: 'min' },
  { abbr: 'NYM', name: 'New York Mets', short: 'Mets', espn: 'nym' },
  { abbr: 'NYY', name: 'New York Yankees', short: 'Yankees', espn: 'nyy' },
  { abbr: 'OAK', name: 'Oakland Athletics', short: 'Athletics', espn: 'oak' },
  { abbr: 'PHI', name: 'Philadelphia Phillies', short: 'Phillies', espn: 'phi' },
  { abbr: 'PIT', name: 'Pittsburgh Pirates', short: 'Pirates', espn: 'pit' },
  { abbr: 'SD',  name: 'San Diego Padres', short: 'Padres', espn: 'sd' },
  { abbr: 'SF',  name: 'San Francisco Giants', short: 'Giants', espn: 'sf' },
  { abbr: 'SEA', name: 'Seattle Mariners', short: 'Mariners', espn: 'sea' },
  { abbr: 'STL', name: 'St. Louis Cardinals', short: 'Cardinals', espn: 'stl' },
  { abbr: 'TB',  name: 'Tampa Bay Rays', short: 'Rays', espn: 'tb' },
  { abbr: 'TEX', name: 'Texas Rangers', short: 'Rangers', espn: 'tex' },
  { abbr: 'TOR', name: 'Toronto Blue Jays', short: 'Blue Jays', espn: 'tor' },
  { abbr: 'WSH', name: 'Washington Nationals', short: 'Nationals', espn: 'wsh' },
]

const byAbbr = {}
const byName = {}
const byShort = {}

TEAMS.forEach(t => {
  byAbbr[t.abbr.toUpperCase()] = t
  byName[t.name.toLowerCase()] = t
  byShort[t.short.toLowerCase()] = t
})

export function findTeam(text) {
  if (!text) return null
  const upper = text.toUpperCase().trim()
  const lower = text.toLowerCase().trim()
  if (byAbbr[upper]) { const t = byAbbr[upper]; return { ...t, logo: ESPN_LOGO(t.espn) } }
  if (byName[lower]) { const t = byName[lower]; return { ...t, logo: ESPN_LOGO(t.espn) } }
  if (byShort[lower]) { const t = byShort[lower]; return { ...t, logo: ESPN_LOGO(t.espn) } }
  return null
}

export function teamLogo(abbr) {
  if (!abbr) return null
  const t = byAbbr[abbr.toUpperCase()]
  if (t) return ESPN_LOGO(t.espn)
  return null
}

export function parseMatchup(text) {
  if (!text) return null
  const clean = text.replace(/\*\*/g, '').replace(/[^a-zA-Z0-9\s@.]/g, '').trim()
  const atMatch = clean.match(/(.+?)\s+(?:@|vs\.?|v\.)\s+(.+?)(?:\s*[—\-–|]|$)/)
  if (atMatch) {
    const awayText = atMatch[1].trim()
    const homeText = atMatch[2].trim()
    const away = findTeam(awayText)
    const home = findTeam(homeText)
    if (away || home) return { away, home, awayText, homeText }
  }
  return null
}

export function findTeamsInText(text) {
  if (!text) return []
  const found = []
  const seen = new Set()
  TEAMS.forEach(t => {
    const patterns = [
      new RegExp(`\\b${t.abbr}\\b`, 'i'),
      new RegExp(t.name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'i'),
    ]
    for (const p of patterns) {
      if (p.test(text) && !seen.has(t.abbr)) {
        seen.add(t.abbr)
        found.push({ ...t, logo: ESPN_LOGO(t.espn) })
      }
    }
  })
  return found
}

export { TEAMS }
